import { z } from "zod";
import { createChildSchema } from "./child.schema.js";
import { createAllergySchema } from "./allergy.schema.js";
import { createMedicationSchema } from "./medication.schema.js";
import { upsertCarePreferencesSchema } from "./care.schema.js";
import { EXPIRATION_PRESETS } from "./sharedLink.schema.js";

export const PROXY_NAME_MAX = 120;
export const PROXY_EMAIL_MAX = 254;
export const MAX_INTAKE_ALLERGIES = 50;
export const MAX_INTAKE_MEDICATIONS = 50;

/**
 * Which sections a medical proxy is allowed to FILL OUT. Same shape as
 * VisibleSections but opposite meaning (write vs read). The server's
 * intakeScopeFilter is the real gate — the client cannot widen scope.
 */
export const writableSectionsSchema = z.object({
  basic: z.boolean().default(true),
  emergencyContacts: z.boolean().default(true),
  allergies: z.boolean().default(true),
  medications: z.boolean().default(true),
  carePreferences: z.boolean().default(true),
});
export type WritableSections = z.infer<typeof writableSectionsSchema>;

export const createIntakeLinkSchema = z.object({
  childId: z.string().uuid("Invalid patient id"),
  proxyName: z
    .string()
    .trim()
    .max(PROXY_NAME_MAX, "Name is too long")
    .nullish()
    .transform((v) => (v && v.length > 0 ? v : null)),
  proxyEmail: z
    .string()
    .trim()
    .max(PROXY_EMAIL_MAX, "Email is too long")
    .email("Invalid email address")
    .nullish()
    .transform((v) => (v && v.length > 0 ? v : null)),
  writableSections: writableSectionsSchema,
  expirationPreset: z.enum(EXPIRATION_PRESETS).default("7d"),
  singleUse: z.boolean().default(true),
});
export type CreateIntakeLinkInput = z.infer<typeof createIntakeLinkSchema>;

/**
 * The proxy's submission payload. Reuses the canonical create schemas so the
 * proxy's input obeys the EXACT same field rules (lengths, phone regex,
 * severity/frequency enums) as authenticated edits — zero validation drift.
 * Arrays are capped to bound the rows a single submit can create; photoUrl is
 * intentionally NOT accepted (keeps the request under the global 64KB body limit).
 */
export const intakeSubmissionSchema = z.object({
  // `legalName` is omitted the same way `rescueMedicationId` is below: the
  // intake surface is unchanged by v2, and a public proxy has no business
  // writing a child's legal name. Zod strips the unknown key if one is sent.
  child: createChildSchema.omit({ legalName: true }).partial(),
  // `rescueMedicationId` is omitted deliberately: it is a FK to a medications
  // row, and a public proxy must never be able to supply a record id. The proxy
  // fills the descriptive FARE fields; the org links the device afterwards.
  allergies: z
    .array(createAllergySchema.omit({ rescueMedicationId: true }))
    .max(MAX_INTAKE_ALLERGIES)
    .optional()
    .default([]),
  medications: z
    .array(createMedicationSchema)
    .max(MAX_INTAKE_MEDICATIONS)
    .optional()
    .default([]),
  carePreferences: upsertCarePreferencesSchema.optional(),
});
export type IntakeSubmissionInput = z.infer<typeof intakeSubmissionSchema>;
